import React, { useState } from "react";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../../components/utils";
import RepricingReportDownload from "./RepricingReportDownload";
import {
  OptionsRepricingDetails,
  RepricingGrantPreviewDetail,
} from "../../../../types/CompanyProfile";
import { useApproveOptionsRepricing } from "../../../../queries/onBoarding";
import {
  formatCurrency,
  getCurrencyType,
} from "../../../../utils/currencyFormatter";

type Props = {
  savedCorporateActionId: string | undefined;
  previewGrants: RepricingGrantPreviewDetail[];
  onBackClick: () => void;
  onClose: () => void;
  reportBase64?: string;
  isRegeneratingReport?: boolean;
};

const WARNING_NOTES = [
  "Only grants with grant date on or before the effective date are included.",
  "Grant Level: the grant's strike price is replaced for all future calculations.",
  "Vesting Level: only unvested tranches at the effective date are updated.",
  "Already-exercised lots retain the original strike price used at exercise time.",
  "An APPROVED repricing can be discarded — this will revert all grant changes.",
  "Ensure all incomplete events are completed before approving.",
];

export default function ApproveRepricing({
  savedCorporateActionId,
  previewGrants,
  onBackClick,
  onClose,
  reportBase64,
  isRegeneratingReport,
}: Props) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const currencyType = getCurrencyType();
  const { mutate: approveRepricing, isLoading } = useApproveOptionsRepricing();
  const [approved, setApproved] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | undefined>();

  const uniqueEmployees = new Set(previewGrants.map((g) => g.employeeId)).size;
  const uniquePlans = new Set(previewGrants.map((g) => g.planId)).size;
  const overrideCount = Object.keys(
    formik.values.grantPriceOverrides || {}
  ).length;

  const canApprove = !!savedCorporateActionId && !approved;

  function handleApprove() {
    if (!savedCorporateActionId) return;
    approveRepricing(savedCorporateActionId, {
      onSuccess: (data) => {
        setApproved(true);
        setReportUrl(data?.reportUrl);
        toast.success("Options repricing approved successfully");
        onClose();
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.errorRaw || "Error approving repricing"
        );
      },
    });
  }

  function tooltipText() {
    if (!savedCorporateActionId)
      return "Save and generate preview before approving";
    if (approved) return "Already approved";
    return "Approving will apply the new strike price to all filtered grants";
  }

  return (
    <VStack className="w-full gap-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">
          Approve Repricing
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Review the summary and approve to apply the repricing
        </p>
      </div>

      {/* Summary card */}
      <VStack className="border border-gray-200 rounded-lg p-5 gap-4">
        <p className="text-sm font-semibold text-gray-700">Repricing Details</p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3">
          <SummaryRow label="Event Name" value={formik.values.name} />
          <SummaryRow label="Effective Date" value={formik.values.date} />
          <SummaryRow
            label="Repricing Type"
            value={
              formik.values.repricingType === "GRANT"
                ? "Grant Level"
                : "Vesting Level"
            }
          />
          <SummaryRow
            label="New Strike Price"
            value={
              formik.values.newStrikePrice
                ? formatCurrency(
                    formik.values.newStrikePrice,
                    currencyType || ""
                  )
                : "—"
            }
          />
          <SummaryRow
            label="Grants Affected"
            value={String(previewGrants.length)}
          />
          <SummaryRow
            label="Employees Affected"
            value={String(uniqueEmployees)}
          />
          <SummaryRow label="Plans Affected" value={String(uniquePlans)} />
          {overrideCount > 0 && (
            <SummaryRow
              label="Individual Overrides"
              value={`${overrideCount} grant(s)`}
            />
          )}
        </div>
      </VStack>

      <VStack className="border border-gray-200 rounded-lg p-5 gap-4">
        <p className="text-sm font-semibold text-gray-700">Summary Report</p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3 md:grid-cols-3">
          {isRegeneratingReport && (
            <p className="text-xs text-gray-400">Generating report…</p>
          )}
          {!isRegeneratingReport && reportBase64 && (
            <RepricingReportDownload base64={reportBase64} />
          )}
          {!isRegeneratingReport && !reportBase64 && reportUrl && (
            <RepricingReportDownload s3url={reportUrl} />
          )}
        </div>
      </VStack>

      {/* Warning notes */}
      <VStack className="p-3 bg-yellow-100 rounded gap-2">
        <HStack className="items-center gap-1 pl-1">
          <Icon
            icon="material-symbols:info-outline-rounded"
            height={18}
            width={18}
            className="text-black-501"
          />
          <p className="text-sm font-medium">Note</p>
        </HStack>
        <Box className="pl-5 pb-1">
          <ul className="list-disc">
            {WARNING_NOTES.map((note, i) => (
              <li key={i} className="text-xs pt-0.5">
                {note}
              </li>
            ))}
          </ul>
        </Box>
      </VStack>

      <HStack className="justify-between mt-4">
        <ButtonPrimary1
          className="h-8 px-4 py-1"
          onClick={onBackClick}
          type="button"
        >
          Back
        </ButtonPrimary1>
        <div className="relative group">
          <ButtonPrimary
            loading={isLoading}
            disabled={!canApprove || isLoading}
            onClick={handleApprove}
            type="button"
          >
            Approve
          </ButtonPrimary>
          {!canApprove && (
            <div className="absolute bottom-0 right-0 flex-col items-center hidden mb-8 group-hover:flex">
              <div className="relative z-20 p-2 text-xs text-black shadow-lg bg-[#f6f9fb] rounded whitespace-nowrap">
                {tooltipText()}
              </div>
              <div className="w-3 h-3 -mt-2 ml-20 rotate-45 bg-[#f6f9fb]" />
            </div>
          )}
        </div>
      </HStack>
    </VStack>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <VStack className="gap-0.5">
      <span className="text-xs text-gray-500 font-medium">{label}</span>
      <span className="text-sm text-gray-800 font-semibold">
        {value || "—"}
      </span>
    </VStack>
  );
}
